/**
 * What each department means to spend, and what is left of it.
 *
 * The bar is the whole screen: posted, committed and pending shown separately,
 * because a budget compared only against posted entries tells a department
 * head they have money they have already promised away. Committed is approved
 * payments and the commitment periods still to fall due this year.
 *
 * A budget refuses nothing. When it is exceeded, the approval rule decides
 * whether that adds a second pair of eyes — and whoever approves the payment
 * sees where the budget stands either way.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, Empty, ErrorNote, Field, Loading, Note, Pill } from '../components/ui'
import SearchSelect from '../components/SearchSelect'

interface Option {
  id: string
  code: string | null
  name: string
  note: string | null
}

interface FormOptions {
  departments: Option[]
  locations: Option[]
  cost_centers: Option[]
  categories: Option[]
}

interface BudgetRow {
  id: string
  reference: string
  fy_code: string
  label: string
  status: string
  version: number
  note: string | null
  amount_paise: number
  department: string | null
  location: string | null
  cost_centre: string | null
  category: string | null
  account: string | null
  budget_paise: number
  actual_paise: number
  committed_paise: number
  pending_paise: number
  used_paise: number
  remaining_paise: number
  used_bp: number
  over: boolean
  approval_state: string
}

function currentFy(): string {
  const now = new Date()
  const start = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`
}

/** Posted, committed and pending as three widths of one bar. */
function Bar({ row }: { row: BudgetRow }) {
  const total = Math.max(row.budget_paise, row.used_paise + row.pending_paise, 1)
  const width = (paise: number) => `${Math.min(100, (paise / total) * 100)}%`
  return (
    <div style={{ minWidth: 160 }}>
      <div
        style={{
          display: 'flex',
          height: 10,
          borderRadius: 5,
          overflow: 'hidden',
          background: 'rgba(127,127,127,.18)',
        }}
      >
        <div style={{ width: width(row.actual_paise), background: '#2f7d4f' }} />
        <div style={{ width: width(row.committed_paise), background: '#b8862b' }} />
        <div style={{ width: width(row.pending_paise), background: 'rgba(127,127,127,.5)' }} />
      </div>
      <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
        {(row.used_bp / 100).toFixed(0)}% used
        {row.pending_paise > 0 && `, ${formatInr(row.pending_paise)} still being decided`}
      </div>
    </div>
  )
}

function NewBudget({ fy, onDone }: { fy: string; onDone: () => void }) {
  const [amount, setAmount] = useState('')
  const [department, setDepartment] = useState('')
  const [location, setLocation] = useState('')
  const [category, setCategory] = useState('')
  const [note, setNote] = useState('')

  const options = useQuery({
    queryKey: ['spend-form-options'],
    queryFn: () => api.get<FormOptions>('/spend/form-options'),
  })
  const create = useMutation({
    mutationFn: () =>
      api.post('/budgets', {
        fy_code: fy,
        amount_paise: Math.round(Number(amount) * 100),
        department_id: department || null,
        location_id: location || null,
        category_id: category || null,
        note: note || null,
      }),
    onSuccess: onDone,
  })

  return (
    <Card title={`A new budget for ${fy}`} hint="a dimension left blank means “any”" tight>
      <ErrorNote error={create.error} />
      <div className="row">
        <Field label="Amount" caption="rupees">
          <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
        </Field>
        <Field label="Department">
          <select value={department} onChange={(e) => setDepartment(e.target.value)}>
            <option value="">Any</option>
            {(options.data?.departments ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Clinic">
          <select value={location} onChange={(e) => setLocation(e.target.value)}>
            <option value="">Any</option>
            {(options.data?.locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Category">
          <SearchSelect
            aria-label="Category"
            value={category}
            onChange={setCategory}
            empty="Any"
            options={(options.data?.categories ?? []).map((c) => ({ value: c.id, label: c.name }))}
          />
        </Field>
        <Field label="Note" caption="what this figure is based on">
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <button
        className="primary"
        disabled={!amount || create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? 'Saving…' : 'Draft this budget'}
      </button>
      <p className="faint" style={{ marginBottom: 0 }}>
        A budget for “Operations” covers the whole department. One for “Operations, Thane,
        medical supplies” is narrower, and the narrowest one is what answers for a payment.
      </p>
    </Card>
  )
}

export default function Budgets() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [fy, setFy] = useState(currentFy())
  const [drafting, setDrafting] = useState(false)

  const rows = useQuery({
    queryKey: ['budgets', fy],
    queryFn: () => api.get<BudgetRow[]>(`/budgets?fy_code=${fy}`),
  })
  const done = () => {
    setDrafting(false)
    void queryClient.invalidateQueries({ queryKey: ['budgets'] })
  }
  const submit = useMutation({
    mutationFn: (id: string) => api.post(`/budgets/${id}/submit`),
    onSuccess: done,
  })

  const live = (rows.data ?? []).filter((row) => row.status === 'approved')
  const over = live.filter((row) => row.over)

  return (
    <>
      <Card
        title={`Budgets for ${fy}`}
        hint={`${live.length} live`}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={fy}
              onChange={(e) => setFy(e.target.value)}
              style={{ width: 100 }}
              placeholder="2026-27"
            />
            {can('budget.budget.create') && (
              <button className="small" onClick={() => setDrafting(!drafting)}>
                {drafting ? 'Close' : 'New budget'}
              </button>
            )}
          </div>
        }
        tight
      >
        <ErrorNote error={rows.error} />
        {rows.isLoading && <Loading what="Loading budgets" />}
        {over.length > 0 && (
          <Note kind="warn">
            {over.length} budget(s) are past their figure. Nothing is refused for being over
            one — whoever approves the payment decides, and sees this.
          </Note>
        )}
        {rows.data && rows.data.length === 0 && (
          <Empty>Nothing is budgeted for {fy} yet.</Empty>
        )}
        {rows.data && rows.data.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Covers</th>
                <th className="num">Budget</th>
                <th className="num">In the ledger</th>
                <th className="num">Committed</th>
                <th className="num">Left</th>
                <th>Used</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.data.map((row) => (
                <tr key={row.id}>
                  <td className="mono">
                    {row.reference}
                    {row.status !== 'approved' && (
                      <div style={{ marginTop: 4 }}>
                        <Pill value={row.status} />
                      </div>
                    )}
                  </td>
                  <td>
                    {row.label}
                    {row.note && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {row.note}
                      </div>
                    )}
                    {row.status === 'pending_approval' && (
                      <div className="faint" style={{ fontSize: 12 }}>
                        {row.approval_state}
                      </div>
                    )}
                  </td>
                  <td className="num">{formatInr(row.amount_paise)}</td>
                  <td className="num">{formatInr(row.actual_paise)}</td>
                  <td className="num">{formatInr(row.committed_paise)}</td>
                  <td className="num">
                    {row.over ? <strong>{formatInr(row.remaining_paise)}</strong> : formatInr(row.remaining_paise)}
                  </td>
                  <td>
                    <Bar row={row} />
                  </td>
                  <td className="num">
                    {row.status === 'draft' && can('budget.budget.submit') && (
                      <button
                        className="small"
                        disabled={submit.isPending}
                        onClick={() => submit.mutate(row.id)}
                      >
                        Submit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <ErrorNote error={submit.error} />
      </Card>

      {drafting && <NewBudget fy={fy} onDone={done} />}

      <Note kind="info">
        Committed is money already promised and not yet in the ledger: approved payments
        waiting to be sent, and the commitment periods still to fall due this year. Only the
        ledger column ties to the accounts — and a budget that ignored the rest of the
        year's rent would say there was room in March.
      </Note>
    </>
  )
}
